import * as React from 'react';
import { Typography, Box, Container, TextField, Button, Avatar, Select, MenuItem, InputLabel, FormControl, Dialog, Paper, Divider, Grid } from '@mui/material';
import { connect } from "react-redux";
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { getOrders, assignOrder } from '../../app/store/actions/imsEmployeesActions';
import moment from 'moment';
import { Inventory, QrCodeScanner, LocalShipping, Assignment } from '@mui/icons-material';
import { useState } from 'react';
import QrReader from "modern-react-qr-reader"
import { isDesktop, isMobile } from 'react-device-detect';
import { notification } from 'antd';

function detectCarrierFromScan(data) {
    const value = String(data || "").trim();
    if (!value) return null;
    if (value.toUpperCase().startsWith("BLK") || value.toUpperCase().startsWith("BLD-")) {
        return { service: "Blink", trackingId: value };
    }
    if (value.includes("-") && !value.toUpperCase().startsWith("BOSTA")) {
        const parts = value.split("-");
        if (parts.length > 1 && parts[1]) {
            return { service: "ShipBlu", trackingId: parts[1] };
        }
    }
    return { service: "Bosta", trackingId: value.replace(/^BOSTA_/i, "") };
}

export const IMSEmployeeHome = ({ user, getOrders, assignOrder, order }) => {

    const navigate = useNavigate()
    const [shipbluId, setShipbluId] = useState(null);
    const [bostaTrackingId, setBostaTrackingId] = useState(null);
    const [blinkTrackingId, setBlinkTrackingId] = useState(null);
    const [scanning, setScanning] = useState(false);
    const [scanText, setScanText] = useState("");
    const [shippingServiceToScan, setShippingServiceToScan] = useState(user.store?.defaultShipping || "Bosta")
    const [inputTimeout, setInputTimeout] = useState(null);

    useEffect(() => {
        if (shipbluId) {
            getOrders({ shipbluId })
        }
    }, [shipbluId])

    useEffect(() => {
        if (bostaTrackingId) {
            getOrders({ bostaTrackingId })
        }
    }, [bostaTrackingId])

    useEffect(() => {
        if (blinkTrackingId) {
            getOrders({ blinkTrackingId })
        }
    }, [blinkTrackingId])

    const startOrder = (order) => {
        if (order.assignee?._id === user._id) {
            navigate(`/ims/employee/order/${order._id}`)
        } else {
            assignOrder({ orderId: order._id }, navigate);
        }
    }

    useEffect(() => {
        if (order && ["Preparing", "New", "Pending Confirmation", "Sold Out"].includes(order.status)) {
            startOrder(order)
        } else if (order && order.status === "Prepared") {
            notification.info({ message: "Order is already prepared" }, { style: { mt: "15vh" } })
        }
    }, [order])

    const applyLookup = (service, trackingId) => {
        if (!trackingId) return;
        if (service === "Blink") {
            setBlinkTrackingId(trackingId);
        } else if (service === "ShipBlu") {
            setShipbluId(trackingId);
        } else {
            // Bosta + Fincart share the same employee lookup (bostaTrackingId / fincartId / fincartCode).
            setBostaTrackingId(trackingId);
        }
        setScanning(false);
    }

    const handleScan = (data) => {
        setScanText(data)
        if (!data) return;

        const detected = detectCarrierFromScan(data);
        // Prefer explicit Blink/ShipBlu detection from QR payload; otherwise use selected carrier.
        if (detected?.service === "Blink" || shippingServiceToScan === "Blink") {
            applyLookup("Blink", detected?.trackingId || String(data).trim());
            return;
        }

        if (isDesktop) {
            if (shippingServiceToScan === "ShipBlu" || detected?.service === "ShipBlu") {
                applyLookup("ShipBlu", detected?.service === "ShipBlu" ? detected.trackingId : data.split("-")[1]);
            } else {
                applyLookup(
                    shippingServiceToScan === "Fincart" ? "Fincart" : "Bosta",
                    detected?.trackingId || data.replace(/^BOSTA_/i, "")
                );
            }
            return;
        }

        // Mobile camera path
        if (detected?.service === "ShipBlu") {
            applyLookup("ShipBlu", detected.trackingId);
            return;
        }

        if (data.length === 7) {
            if (inputTimeout) clearTimeout(inputTimeout);
            const timeout = setTimeout(() => {
                applyLookup(
                    shippingServiceToScan === "Fincart" ? "Fincart" : "Bosta",
                    data.replace(/^BOSTA_/i, "")
                );
            }, 1000);
            setInputTimeout(timeout);
            return;
        }

        applyLookup(
            shippingServiceToScan === "ShipBlu"
                ? "ShipBlu"
                : shippingServiceToScan === "Fincart"
                    ? "Fincart"
                    : "Bosta",
            detected?.trackingId || data.replace(/^BOSTA_/i, "")
        );
    }

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Paper elevation={0} sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                            {user?.name?.[0]}
                        </Avatar>
                        <Box>
                            <Typography variant="h4" fontWeight="bold" color="primary">
                                مرحباً {user?.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                نظام تجهيز الطلبات
                            </Typography>
                        </Box>
                    </Box>
                </Paper>

                <Paper elevation={0} sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                        امسح الكود الموجود على بوليصة الشحن
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        قم بمسح رمز QR الموجود على بوليصة الشحن لبدء تجهيز الطلب
                    </Typography>

                    <Grid container spacing={2} sx={{ mt: 2 }}>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>شركة التوصيل</InputLabel>
                                <Select
                                    value={shippingServiceToScan}
                                    label="شركة التوصيل"
                                    onChange={({ target }) => setShippingServiceToScan(target.value)}
                                    startAdornment={
                                        <LocalShipping sx={{ mr: 1, color: 'primary.main' }} />
                                    }
                                >
                                    <MenuItem value="ShipBlu">ShipBlu</MenuItem>
                                    <MenuItem value="Bosta">Bosta</MenuItem>
                                    <MenuItem value="Fincart">Fincart</MenuItem>
                                    <MenuItem value="Blink">Blink</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                autoFocus
                                value={scanText}
                                label="رمز QR"
                                onChange={({ target }) => setScanText(target.value)}
                                InputProps={{
                                    startAdornment: <QrCodeScanner sx={{ mr: 1, color: 'primary.main' }} />
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Tab' || e.key === 'Enter') {
                                        e.preventDefault();
                                        handleScan(scanText);
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>

                    {isMobile && (
                        <Button
                            variant="contained"
                            fullWidth
                            size="large"
                            onClick={() => setScanning(true)}
                            startIcon={<QrCodeScanner />}
                            sx={{ mt: 2 }}
                        >
                            ابدأ مسح الكود
                        </Button>
                    )}
                </Paper>

                {order && (
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Assignment sx={{ color: 'primary.main', fontSize: 40 }} />
                            <Box>
                                <Typography variant="h6" fontWeight="bold">
                                    {order.status === "New" ? "طلب جديد" :
                                        order.status === "Preparing" ? "طلب تحت التنفيذ" :
                                            "طلب جاهز"}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {order.blinkTrackingId || order.bostaTrackingId || order.courierTrackingId || order.fincartCode || order.shipbluTrackingId || order.externalId}
                                </Typography>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    {order.status === "New" ?
                                        `تم الإنشاء ${moment(order.createdAt).fromNow()}` :
                                        `تم التعيين ${moment(order.assignedAt).fromNow()}`
                                    }
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => startOrder(order)}
                                startIcon={<Inventory />}
                                disabled={order.status === "Prepared"}
                            >
                                {order.status === "New" ? "عين الطلب" :
                                    order.status === "Preparing" ? "جهز الطلب" :
                                        "تم التجهيز"}
                            </Button>
                        </Box>
                    </Paper>
                )}
            </Box>

            <Dialog
                open={scanning}
                onClose={() => setScanning(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 2 }
                }}
            >
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                        امسح الكود الموجود على بوليصة الشحن
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        قم بتوجيه الكاميرا نحو رمز QR
                    </Typography>

                    <Box sx={{ my: 3 }}>
                        <QrReader
                            onScan={handleScan}
                            style={{
                                height: 300,
                                width: 300,
                                borderRadius: 8,
                                overflow: 'hidden'
                            }}
                            facingMode={"environment"}
                        />
                    </Box>

                    <Button
                        variant="outlined"
                        onClick={() => setScanning(false)}
                        fullWidth
                    >
                        إلغاء
                    </Button>
                </Box>
            </Dialog>
        </Container>
    );
}

const mapStateToProps = (state) => ({
    user: state?.auth?.user,
    warehouses: state?.records?.warehouses,
    order: state?.records?.order
});

const mapDispatchToProps = { getOrders, assignOrder };

export default connect(mapStateToProps, mapDispatchToProps)(IMSEmployeeHome);
